/**
 * Hook de détection de triche pour l'entretien structuré
 * Collecte les métadonnées comportementales pendant l'entretien:
 * - Temps de réponse
 * - Vitesse de frappe
 * - Détection de copier-coller
 * - Changements d'onglet/visibilité
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  calculerScoreSuspicion,
  calculerVitesseFrappe,
  type CheatingMeta,
} from "@repo/utils";

// ============================================
// TYPES
// ============================================

interface Absence {
  debut: number;
  fin: number;
  duree: number;
}

interface CheatingDetectionState {
  // Temps
  questionAfficheeAt: number | null; // Timestamp d'affichage de la question
  premierCaractereAt: number | null; // Timestamp du premier caractère
  derniereFrappeAt: number | null; // Timestamp de la dernière frappe

  // Frappe
  nombreCaracteres: number;
  nombreEdits: number;
  pasteDetecte: boolean;

  // Navigation
  changementsOnglet: number;
  tempsHorsPage: number;
  absences: Absence[];

  // État de tracking
  isTracking: boolean;
  absenceEnCours: { debut: number } | null;
}

interface UseCheatingDetectionReturn {
  // À appeler quand le bot envoie un message (nouvelle question)
  onQuestionAffichee: () => void;
  // À appeler à chaque frappe clavier
  onKeystroke: (charCount?: number) => void;
  // À appeler sur événement paste
  onPaste: () => void;
  // À appeler avant d'envoyer le message - retourne les métadonnées
  getCheatingMeta: (messageLength: number) => CheatingMeta;
  // Reset pour la prochaine question
  reset: () => void;
  // Score de suspicion actuel (pour affichage temps réel si besoin)
  currentScore: number;
}

// ============================================
// HOOK
// ============================================

export function useCheatingDetection(): UseCheatingDetectionReturn {
  const [state, setState] = useState<CheatingDetectionState>({
    questionAfficheeAt: null,
    premierCaractereAt: null,
    derniereFrappeAt: null,
    nombreCaracteres: 0,
    nombreEdits: 0,
    pasteDetecte: false,
    changementsOnglet: 0,
    tempsHorsPage: 0,
    absences: [],
    isTracking: false,
    absenceEnCours: null,
  });

  // Référence pour le tracking de visibilité
  const visibilityRef = useRef<{
    changementsOnglet: number;
    tempsHorsPage: number;
    absences: Absence[];
    absenceEnCours: { debut: number } | null;
  }>({
    changementsOnglet: 0,
    tempsHorsPage: 0,
    absences: [],
    absenceEnCours: null,
  });

  // ============================================
  // TRACKING DE VISIBILITÉ (onglet)
  // ============================================

  useEffect(() => {
    const handleVisibilityChange = () => {
      const now = Date.now();

      if (document.hidden) {
        // L'utilisateur quitte la page
        visibilityRef.current.absenceEnCours = { debut: now };
        visibilityRef.current.changementsOnglet++;
      } else {
        // L'utilisateur revient sur la page
        if (visibilityRef.current.absenceEnCours) {
          const absence: Absence = {
            debut: visibilityRef.current.absenceEnCours.debut,
            fin: now,
            duree: now - visibilityRef.current.absenceEnCours.debut,
          };
          visibilityRef.current.absences.push(absence);
          visibilityRef.current.tempsHorsPage += absence.duree;
          visibilityRef.current.absenceEnCours = null;
        }
      }

      // Mettre à jour le state
      setState((prev) => ({
        ...prev,
        changementsOnglet: visibilityRef.current.changementsOnglet,
        tempsHorsPage: visibilityRef.current.tempsHorsPage,
        absences: [...visibilityRef.current.absences],
        absenceEnCours: visibilityRef.current.absenceEnCours,
      }));
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // ============================================
  // MÉTHODES PUBLIQUES
  // ============================================

  /**
   * À appeler quand le bot envoie un message (nouvelle question affichée)
   */
  const onQuestionAffichee = useCallback(() => {
    const now = Date.now();

    // Reset le tracking pour la nouvelle question
    setState((prev) => ({
      ...prev,
      questionAfficheeAt: now,
      premierCaractereAt: null,
      derniereFrappeAt: null,
      nombreCaracteres: 0,
      nombreEdits: 0,
      pasteDetecte: false,
      isTracking: true,
    }));

    // Reset aussi le tracking de visibilité pour cette question
    visibilityRef.current = {
      changementsOnglet: 0,
      tempsHorsPage: 0,
      absences: [],
      absenceEnCours: null,
    };
  }, []);

  /**
   * À appeler à chaque frappe clavier
   */
  const onKeystroke = useCallback((charCount: number = 1) => {
    const now = Date.now();

    setState((prev) => {
      if (!prev.isTracking) return prev;

      const isFirstKeystroke = prev.premierCaractereAt === null;
      const newEdits =
        prev.nombreCaracteres > 0 ? prev.nombreEdits + 1 : prev.nombreEdits;

      return {
        ...prev,
        premierCaractereAt: isFirstKeystroke ? now : prev.premierCaractereAt,
        derniereFrappeAt: now,
        nombreCaracteres: prev.nombreCaracteres + charCount,
        nombreEdits: newEdits,
      };
    });
  }, []);

  /**
   * À appeler sur événement paste détecté
   */
  const onPaste = useCallback(() => {
    setState((prev) => ({
      ...prev,
      pasteDetecte: true,
    }));
  }, []);

  /**
   * Calcule et retourne les métadonnées de triche
   * À appeler avant d'envoyer le message
   */
  const getCheatingMeta = useCallback(
    (messageLength: number): CheatingMeta => {
      const now = Date.now();

      // Calculer les temps
      const tempsAffichageQuestion =
        state.questionAfficheeAt && state.premierCaractereAt
          ? state.premierCaractereAt - state.questionAfficheeAt
          : 0;

      const tempsRedaction =
        state.premierCaractereAt && state.derniereFrappeAt
          ? state.derniereFrappeAt - state.premierCaractereAt
          : 0;

      const tempsTotal = state.questionAfficheeAt
        ? now - state.questionAfficheeAt
        : 0;

      // Calculer la vitesse de frappe
      const vitesseFrappe =
        tempsRedaction > 0
          ? calculerVitesseFrappe(messageLength, tempsRedaction)
          : 0;

      // Préparer les métadonnées pour le calcul du score
      const metaInput = {
        tempsAffichageQuestion,
        tempsRedaction,
        tempsTotal,
        vitesseFrappe,
        nombreEdits: state.nombreEdits,
        pasteDetecte: state.pasteDetecte,
        changementsOnglet: visibilityRef.current.changementsOnglet,
        tempsHorsPage: visibilityRef.current.tempsHorsPage,
        absences: [...visibilityRef.current.absences],
        longueurMessage: messageLength,
      };

      // Calculer le score de suspicion
      const scoreSuspicion = calculerScoreSuspicion(metaInput);

      return {
        tempsAffichageQuestion,
        tempsRedaction,
        tempsTotal,
        vitesseFrappe,
        nombreEdits: state.nombreEdits,
        pasteDetecte: state.pasteDetecte,
        changementsOnglet: visibilityRef.current.changementsOnglet,
        tempsHorsPage: visibilityRef.current.tempsHorsPage,
        absences: [...visibilityRef.current.absences],
        longueurMessage: messageLength,
        scoreSuspicion,
      };
    },
    [state],
  );

  /**
   * Reset complet pour la prochaine question
   */
  const reset = useCallback(() => {
    setState({
      questionAfficheeAt: null,
      premierCaractereAt: null,
      derniereFrappeAt: null,
      nombreCaracteres: 0,
      nombreEdits: 0,
      pasteDetecte: false,
      changementsOnglet: 0,
      tempsHorsPage: 0,
      absences: [],
      isTracking: false,
      absenceEnCours: null,
    });

    visibilityRef.current = {
      changementsOnglet: 0,
      tempsHorsPage: 0,
      absences: [],
      absenceEnCours: null,
    };
  }, []);

  // Calculer le score actuel pour affichage temps réel
  const currentScore = state.questionAfficheeAt
    ? calculerScoreSuspicion({
        tempsAffichageQuestion:
          state.questionAfficheeAt && state.premierCaractereAt
            ? state.premierCaractereAt - state.questionAfficheeAt
            : 0,
        tempsRedaction:
          state.premierCaractereAt && state.derniereFrappeAt
            ? state.derniereFrappeAt - state.premierCaractereAt
            : 0,
        tempsTotal: state.questionAfficheeAt
          ? Date.now() - state.questionAfficheeAt
          : 0,
        vitesseFrappe: 0, // Sera recalculé avec la longueur finale
        nombreEdits: state.nombreEdits,
        pasteDetecte: state.pasteDetecte,
        changementsOnglet: visibilityRef.current.changementsOnglet,
        tempsHorsPage: visibilityRef.current.tempsHorsPage,
        absences: visibilityRef.current.absences,
        longueurMessage: state.nombreCaracteres || 1,
      })
    : 0;

  return {
    onQuestionAffichee,
    onKeystroke,
    onPaste,
    getCheatingMeta,
    reset,
    currentScore,
  };
}
